import React, { ReactNode, useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import moment from "moment";
import {
  DisplayCurrency,
  FormattedChartDataPoint,
  LanguageNameSpaces,
  TimeRange,
  Timeframe,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  BASE_CHART_TIME_RANGES,
  TIME_RANGE_1_DAY,
  TIME_RANGE_START,
} from "@shared/common/constants";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import { DISPLAY_CURRENCIES } from "common/constants";
import { compactAxisValue } from "features/bots/NewLook/newLookBotUtils";
import useMeasure from "utils/hooks/useMeasure";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import { PhoneHeroRanges } from "features/dashboard/PhoneDashboard/PhoneHeroControls.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import ChartEmpty from "common/NewLook/ChartEmpty.tsx";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import NewLookProfitChart, {
  ProfitPoint,
} from "features/bots/NewLook/NewLookProfitChart";
import { formatDateLabel } from "@shared/utils/date";

interface NewLookBotChartProps {
  graphData?: FormattedChartDataPoint[];
  isFetching?: boolean;
  isPercent?: boolean;
  isLineChart?: boolean;
  displayCurrency: DisplayCurrency;
  precision?: number;
  timeRange: TimeRange;
  /** Omitted: no range control (a backtest's fixed period). */
  setTimeRange?: (timeRange: TimeRange) => void;
  /** Left of the header, e.g. the chart-type switch. */
  title?: ReactNode;
  /** A running backtest's period, which the axis spans. */
  timeframe?: Timeframe;
}

const TIME_RANGES = [
  TIME_RANGE_START,
  ...BASE_CHART_TIME_RANGES,
] as TimeRange[];

/**
 * New look: one bot's profit chart (a bot's drawer, a public bot, a
 * backtest) in the platform's chart style. The header holds the chart-type
 * switch and the ranges; a quiet line under it reads out the range's change,
 * or the point under the crosshair.
 */
const NewLookBotChart = ({
  graphData,
  isFetching,
  isPercent,
  isLineChart = true,
  displayCurrency,
  precision = 2,
  timeRange,
  setTimeRange,
  title,
  timeframe,
}: NewLookBotChartProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const [areaRef, { height }] = useMeasure<HTMLDivElement>();
  const [hovered, setHovered] = useState<ProfitPoint>();
  const symbol = displayCurrency === DISPLAY_CURRENCIES.BTC ? "₿" : "$";

  const points = useMemo(
    () =>
      (graphData || [])
        .filter((point) => !!point && point.rawX !== undefined)
        .map((point) => ({ time: Number(point.rawX), value: Number(point.y) })),
    [graphData]
  );
  const intraday = timeRange === TIME_RANGE_1_DAY;
  const minus = (value: number) => (value < 0 ? "−" : "");
  const formatAxisValue = (value: number, step: number) =>
    compactAxisValue(
      value,
      step,
      isPercent ? { suffix: "%" } : { prefix: symbol }
    );
  const formatValue = (value: number) => {
    const sign = value > 0 ? "+" : minus(value);
    return isPercent
      ? `${sign}${Math.abs(value).toFixed(2)}%`
      : `${sign}${symbol}${getLocaleStringWithPrecision(
          Math.abs(value),
          precision
        )}`;
  };

  // The readout: the range's change (or, for daily bars, their sum); under
  // the crosshair, the change up to that point (or, for bars, that day's
  // profit). Since the start the bot began at 0, so the change is the
  // accumulated value itself (the series' first point may already carry
  // some profit); a shorter range counts from its first point.
  const isStart = timeRange === TIME_RANGE_START;
  const first = points[0];
  const baseline = isStart ? 0 : first?.value ?? 0;
  const last = points[points.length - 1]?.value ?? 0;
  const rangeValue = isLineChart
    ? last - baseline
    : points.reduce((sum, point) => sum + point.value, 0);
  const formatTime = (time: number) =>
    formatDateLabel(
      moment.unix(time),
      intraday ? "MMM D, HH:mm" : "MMM D, YYYY"
    );
  const readoutValue = hovered
    ? isLineChart
      ? hovered.value - baseline
      : hovered.value
    : rangeValue;
  const readoutMeta =
    hovered && !isLineChart
      ? t("performance.on_day", { time: formatTime(hovered.time) })
      : isStart
      ? t("performance.since_start")
      : hovered && first
      ? t("performance.since", { time: formatTime(first.time) })
      : t("performance.over_range", { range: timeRange.toUpperCase() });

  const { isPhone } = theme;
  const changeRange = (range: TimeRange, set: (range: TimeRange) => void) => {
    setHovered(undefined);
    set(range);
  };
  const rangeLabel = (range: TimeRange) =>
    range === TIME_RANGE_START
      ? t("performance.range_all")
      : range.toUpperCase();

  const renderChart = () => {
    if (!height) return null;
    if (isFetching && points.length < 2) {
      return <Skeleton width="100%" height={height} radius={8} />;
    }
    if (points.length < 2) {
      return (
        <ChartEmpty height={height}>{t("performance.no_history")}</ChartEmpty>
      );
    }
    return (
      <NewLookProfitChart
        points={points}
        height={height}
        mode={isLineChart ? "line" : "bars"}
        intraday={intraday}
        formatAxisValue={formatAxisValue}
        onActiveChange={setHovered}
        timeDomain={timeframe ? [timeframe.start, timeframe.end] : undefined}
        ariaLabel={t(
          isLineChart
            ? "performance.bot_chart_label"
            : "performance.bot_daily_chart_label"
        )}
      />
    );
  };

  return (
    <Wrapper>
      {(title || (setTimeRange && !isPhone)) && (
        <Header>
          <div>{title}</div>
          {setTimeRange && !isPhone && (
            <SegmentTabs<TimeRange>
              options={TIME_RANGES}
              value={timeRange}
              onChange={(range) => changeRange(range, setTimeRange)}
              ariaLabel={t("performance.range_label")}
              getLabel={rangeLabel}
            />
          )}
        </Header>
      )}
      {points.length > 1 && (
        <Readout>
          <ReadoutValue style={{ color: directionColor(tokens, readoutValue) }}>
            {formatValue(readoutValue)}
          </ReadoutValue>
          <ReadoutMeta>{readoutMeta}</ReadoutMeta>
        </Readout>
      )}
      <Area ref={areaRef}>{renderChart()}</Area>
      {setTimeRange && isPhone && (
        <PhoneHeroRanges<TimeRange>
          options={TIME_RANGES}
          value={timeRange}
          onChange={(range) => changeRange(range, setTimeRange)}
          label={t("performance.range_label")}
          surface="panel"
          getLabel={rangeLabel}
        />
      )}
    </Wrapper>
  );
};

export default React.memo(NewLookBotChart);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-bottom: 12px;
`;
const Readout = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 8px;
`;
const ReadoutValue = styled.span`
  ${newLookType.meta};
  font-size: 13px;
  font-weight: 500;
`;
const ReadoutMeta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Area = styled.div`
  position: relative;
  flex: 1;
  min-height: 160px;
`;
